import React from "react"
import {
  ArrayInput,
  BooleanInput,
  FormTab,
  NumberInput,
  required,
  SelectInput,
  SimpleFormIterator,
  SimpleFormProps,
  TabbedForm,
  TextInput,
} from "react-admin"

import { SelectTimezoneInput } from "../../../inputs"
import { OpcoRefInput } from "../companies/OpcoRefInput"
import { SPVRefInput } from "../companies/SPVRefInput"

type Props = Partial<SimpleFormProps> & {
  isExisting: boolean
}

export const statusChoices = [
  { id: "live", name: "Live" },
  { id: "not_live", name: "Not live" },
]

export const typeChoices = [
  { id: "apartment", name: "Apartment" },
  { id: "chalet", name: "Chalet" },
  { id: "villa", name: "Villa" },
]

export const poolAndSaunaTypeChoices = [
  { id: "none", name: "None" },
  { id: "shared", name: "Shared" },
  { id: "private", name: "Private" },
  { id: "shared_and_private", name: "Shared and private" },
]

export const primaryEnergySourceChoices = [
  { id: "gas", name: "Gas" },
  { id: "oil", name: "Oil" },
]

export const primaryHeatingSystemChoices = [
  { id: "heat_pump", name: "Heat pump" },
  { id: "electricity", name: "Electricity" },
]

export const airConditioningSystemChoices = [
  { id: "ac", name: "AC" },
  { id: "ac_with_heating", name: "AC with heating" },
]

export const PropertyForm: React.FC<Props> = ({ isExisting = false, defaultValues, ...rest }) => {
  // const { isValid, isSubmitting } = useFormState()

  return (
    <TabbedForm defaultValues={defaultValues}>
      <FormTab label="General">
        <TextInput
          source="short_id"
          label="MYNE Short ID"
          validate={[required()]}
          helperText="Property short ID, eg. DE0025A"
        />

        <TextInput
          source="internal_name"
          fullWidth
          helperText="Internal property name, will NOT be displayed to the customer"
        />

        <TextInput
          source="name"
          fullWidth
          validate={[required()]}
          helperText="Will be displayed to the customer, eg. inside the app"
        />

        <OpcoRefInput />
        <SPVRefInput />

        <NumberInput source="max_guests_count" />

        <SelectInput source="status" choices={statusChoices} validate={[required()]} />

        <TextInput inputProps={{ maxLength: 2, minLength: 2 }} source="country_code" />

        <SelectInput source="type" choices={typeChoices} validate={[required()]} />

        <BooleanInput source="is_available_for_external_rental" />

        <BooleanInput source="is_holiday_exchange" />

        <TextInput source="address_apartment_no" />

        <TextInput
          multiline
          rows={3}
          source="info_text"
          helperText="Displayed to the customer, eg. inside the app"
          fullWidth
        />

        {/*
        {!isExisting && (
          <div>
            <NumberInput
              fullWidth
              source="total_shares_count"
              helperText="Initial creates this amount of shares for the property. Shares can also be added later."
              validate={[required()]}
            />
          </div>
        )}
        */}
      </FormTab>

      <FormTab label="Amenities">
        <TextInput fullWidth source="amenities.gmaps_url" label="Google Maps URL" />
        <TextInput fullWidth source="amenities.client_profiling_url" label="Client profiling URL" />
        <TextInput fullWidth source="amenities.gdrive_folder_url" label="Google Drive folder URL" />
        <NumberInput fullWidth source="amenities.pax_adults" label="PAX adults" />
        <NumberInput fullWidth source="amenities.pax_children" label="PAX children" />
        <NumberInput fullWidth source="amenities.pax_infants" label="PAX infants" />
        <TextInput fullWidth label="Operating company" source="amenities.operating_company" />
        <TextInput
          fullWidth
          label="Property management company"
          source="amenities.property_mgmt_company"
        />
        <TextInput
          fullWidth
          label="Property management company phone"
          source="amenities.property_mgmt_company_phone"
        />
        <TextInput
          fullWidth
          label="Property management company email"
          source="amenities.property_mgmt_company_email"
        />
        <NumberInput fullWidth label="Plot area sqm" source="amenities.plot_area_sqm" />
        <NumberInput fullWidth label="Living area sqm" source="amenities.living_area_sqm" />
        <BooleanInput
          fullWidth
          label="Has private parking lot"
          source="amenities.has_private_parking_lot"
        />
        <TextInput
          fullWidth
          label="Private parking lot notes"
          source="amenities.private_parking_lot_notes"
        />
        <BooleanInput
          fullWidth
          label="Has public parking lot"
          source="amenities.has_public_parking_lot"
        />
        <TextInput
          fullWidth
          label="Public parking lot notes"
          source="amenities.public_parking_lot_notes"
        />
        <SelectInput
          fullWidth
          label="Pool type"
          source="amenities.pool_type"
          choices={poolAndSaunaTypeChoices}
        />
        <SelectInput
          fullWidth
          label="Sauna type"
          source="amenities.sauna_type"
          choices={poolAndSaunaTypeChoices}
        />
        <SelectInput
          fullWidth
          label="Primary energy source"
          source="amenities.primary_energy_source"
          choices={primaryEnergySourceChoices}
        />
        <TextInput fullWidth label="Energy source notes" source="amenities.energy_source_notes" />
        <BooleanInput fullWidth label="Has solar system" source="amenities.has_solar_system" />
        <TextInput fullWidth label="Solar system notes" source="amenities.solar_system_notes" />
        <SelectInput
          fullWidth
          label="Primary heating system"
          source="amenities.primary_heating_system"
          choices={primaryHeatingSystemChoices}
        />
        <TextInput fullWidth label="Heating system notes" source="amenities.heating_system_notes" />

        <SelectInput
          fullWidth
          label="Air conditioning system"
          source="amenities.air_conditioning_system"
          choices={airConditioningSystemChoices}
        />
        <BooleanInput fullWidth label="Has whirlpool" source="amenities.has_whirlpool" />
        <TextInput fullWidth label="Whirlpool notes" source="amenities.whirlpool_notes" />
      </FormTab>

      <FormTab label="Address">
        <TextInput
          source="address_field_1"
          label="Address Field 1"
          fullWidth
          helperText="E.g. Street + No. for Germany"
          validate={[required()]}
        />
        <TextInput source="address_field_2" label="Address Field 2" fullWidth helperText={false} />

        <TextInput
          source="address_city"
          label="City"
          fullWidth
          helperText={false}
          validate={[required()]}
        />
        <TextInput
          source="address_postal_code"
          label="Postal Code"
          fullWidth
          helperText={false}
          validate={[required()]}
        />

        <TextInput
          source="address_country"
          label="Country"
          fullWidth
          validate={[required()]}
          helperText="Full country name, eg. `Österreich`"
        />
      </FormTab>

      <FormTab label="Location">
        <NumberInput source="address_lat" helperText="Map pin. Latitude" validate={[required()]} />
        <NumberInput
          source="address_long"
          helperText="Map pin. Longitude"
          validate={[required()]}
        />
        <SelectTimezoneInput
          source="timezone"
          helperText="Timezone of the location of the property."
          validate={[required()]}
        />
      </FormTab>

      {/*
      <Box display={{ xs: "block", sm: "flex" }}>
        <Box flex={1} mr={{ xs: 0, sm: "0.5em" }}>
          <CountryRefInput fullWidth source="country_id" />
        </Box>
        <Box flex={2} ml={{ xs: 0, sm: "0.5em" }}>
          <RegionRefInput fullWidth source="region_id" />
        </Box>
        <Box flex={2} ml={{ xs: 0, sm: "0.5em" }}>
          <AreaRefInput fullWidth source="area_id" />
        </Box>
      </Box>
      */}

      <FormTab label="Images">
        <TextInput fullWidth source="card_image_url" helperText="Take URL from Storyblok" />
        <TextInput fullWidth source="header_image_url" helperText="Take URL from Storyblok" />
        <TextInput fullWidth source="thumb_image_url" helperText="Take URL from Storyblok" />
      </FormTab>

      <FormTab label="Stay Info">
        <TextInput source="wifi_ssid" label="Wifi name (SSID)" fullWidth helperText={false} />
        <TextInput source="wifi_password" label="Wifi password" fullWidth helperText={false} />
        <TextInput source="door_code" label="Code to open the door" fullWidth helperText={false} />
        <TextInput
          source="owners_guide_url"
          label="Owners Guide URL"
          fullWidth
          helperText={false}
        />

        <ArrayInput source="quick_infos">
          <SimpleFormIterator inline>
            <TextInput source="title" validate={[required()]} />
            <TextInput source="description" />
            <TextInput source="url" helperText="URLs must begin with https://" />
          </SimpleFormIterator>
        </ArrayInput>
      </FormTab>

      {/*
                  <div>
                    <NullableBooleanInput source="has_newsletter" resource="customers" />
                  </div>
                  */}

      {/*
                  <Box mt="1em" />

                  <Typography variant="h6" gutterBottom>
                    Quotas
                  </Typography>

                  {isExisting && (
                    <ReferenceManyField
                      // source="id"
                      resource="booking_quotas"
                      reference="booking_quotas"
                      basePath="/booking_quotas"
                      // record={formProps.record}
                      target="property_id"
                      addLabel={false}
                      sort={{ field: "created_at", order: "ASC" }}
                    >
                      <Datagrid rowClick="edit">
                        <OwnerRefField source="customer_id" />

                        <TextField source="title" />
                        <NumberField source="share_count" />

                        <DateField source="created_at" showTime />
                      </Datagrid>
                    </ReferenceManyField>
                  )}
                  */}
      {/* <AddQuotaUserButton /> */}

      {/*
          <Toolbar
            record={record}
            // undoable={true}
            invalid={!isValid}
            handleSubmit={handleSubmit}
            saving={isSubmitting}
            resource="properties"
          />
          */}
    </TabbedForm>
  )
}

// export const PropertyForm: React.FC<Props> = ({ isExisting = false, ...rest }) => {
//   return (
//     <CompactForm layoutComponents={[RaBox]}>
//       <RaBox display="flex-column" justifyContent="space-between">
//         <RaBox display="flex" justifyContent="space-between">
//           <RaBox display="flex-column" justifyContent="space-around">
//             <Typography variant="h5">General</Typography>

//             <BooleanInput source="is_published" />
//             <BooleanInput source="is_featured" />

//             <TextInput source="title" />
//             <TextInput source="description_title" />
//             <TextInput multiline rows={3} source="description" />
//             <TextInput source="location_title" />
//             <TextInput source="location_description" />
//             <TextInput source="highlights_title" />
//             <TextInput source="highlights_description" />
//           </RaBox>

//           <RaBox display="flex-column" justifyContent="space-around">
//             <Typography variant="h5">Location</Typography>
//             <TextInput source="country_code" />
//             <TextInput source="region_code" />
//             <TextInput source="area_code" />
//           </RaBox>

//           <RaBox display="flex-column" justifyContent="space-around">
//             <Typography variant="h5">Address</Typography>
//             <TextInput source="address_field_1" />
//             <TextInput source="address_field_2" />
//             <TextInput source="address_city" />
//             <TextInput source="address_postal_code" />
//             <TextInput source="address_country" />
//             <NumberInput source="address_lat" />
//             <NumberInput source="address_long" />
//           </RaBox>
//         </RaBox>
//       </RaBox>

//       <RaBox display="flex" justifyContent="space-around">
//         <RaBox display="flex-column" justifyContent="space-around">
//           <Typography variant="h5">Attributes</Typography>

//           <SelectPropertyTypeInput source="property_type" />
//           <SelectPropertyStyleInput source="property_style" />
//           <SelectPropertyVicinitiesInput source="property_vicinities" />
//           <SelectPropertyActivityInput source="activities" />
//           <SelectPropertyAttributesOtherInput source="tags" />
//           <TextInput source="property_attribute_tags" />

//           <TextInput source="construction_year" />
//           <TextInput source="living_area_sqm" />
//           <TextInput source="plot_area_sqm" />
//           <TextInput source="rooms_count" />
//           <TextInput source="bedrooms_count" />
//           <TextInput source="bathrooms_count" />
//         </RaBox>

//         <RaBox display="flex-column" justifyContent="space-around">
//           <Typography variant="h5">Invest</Typography>
//           <TextInput source="total_shares_count" />
//           <TextInput source="salesprice_full" />
//           <TextInput source="salesprice_share" />
//           <TextInput source="salesprice_currency" />
//         </RaBox>
//       </RaBox>

//       {isExisting ? <TextInput source="id" disabled /> : null}
//     </CompactForm>

/*
    <TabbedForm {...rest} redirect={isExisting ? "show" : "list"}>
      <FormTab label="General">
        {isExisting ? <TextInput source="id" disabled /> : null}

        <BooleanInput source="is_published" />
        <BooleanInput source="is_featured" />

        <TextInput source="title" />
        <TextInput source="description_title" />
        <TextInput multiline rows={3} source="description" />
        <TextInput source="location_title" />
        <TextInput source="location_description" />
        <TextInput source="highlights_title" />
        <TextInput source="highlights_description" />
      </FormTab>

      <FormTab label="Location">
        <TextInput source="country_code" />
        <TextInput source="region_code" />
        <TextInput source="area_code" />

        <TextInput source="address_field_1" />
        <TextInput source="address_field_2" />
        <TextInput source="address_city" />
        <TextInput source="address_postal_code" />
        <TextInput source="address_country" />
        <NumberInput source="address_lat" />
        <NumberInput source="address_long" />
      </FormTab>

      <FormTab label="Attributes">
        <SelectPropertyTypeInput source="property_type" />
        <SelectPropertyStyleInput source="property_style" />
        <SelectPropertyVicinitiesInput source="property_vicinities" />
        <SelectPropertyActivityInput source="activities" />
        <SelectPropertyAttributesOtherInput source="tags" />
        <TextInput source="property_attribute_tags" />

        <TextInput source="construction_year" />
        <TextInput source="living_area_sqm" />
        <TextInput source="plot_area_sqm" />
        <TextInput source="rooms_count" />
        <TextInput source="bedrooms_count" />
        <TextInput source="bathrooms_count" />
      </FormTab>

      <FormTab label="Invest">
        <TextInput source="total_shares_count" />
        <TextInput source="salesprice_full" />
        <TextInput source="salesprice_share" />
        <TextInput source="salesprice_currency" />
      </FormTab>
    </TabbedForm>
    */
//   )
// }
